package link.erpango.quip.data.remote.repository.myspareparts

import io.ktor.client.call.ReceivePipelineException
import io.ktor.client.plugins.ResponseException
import io.ktor.client.request.request
import io.ktor.client.statement.bodyAsText
import io.ktor.http.encodeURLParameter
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import link.erpango.quip.data.remote.ApiClient
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.data.remote.repository.IApiRepository
import link.erpango.quip.domain.entities.DMySparePart
import link.erpango.quip.domain.entities.DShareResult
import link.erpango.quip.domain.entities.DShareSparePartRequest
import link.erpango.quip.domain.entities.DShareSparePartResponse

class MySparePartsRepository(
    override val api: ApiClient,
): IMySparePartsRepository, IApiRepository<ApiClient> {
    override val resourcePath: String
        get() = "api/my-spare-parts"

    /**
     * Lista los repuestos personales del usuario, opcionalmente filtrados.
     * Solo se añaden a la query string los parámetros no nulos.
     */
    override suspend fun listMySpareParts(
        vehicleVin: String?,
        brandId: Int?,
        modelId: Int?,
        search: String?
    ): DResponse<List<DMySparePart>> {
        val query = buildQuery(vehicleVin, brandId, modelId, search)
        val url = if (query.isEmpty()) "$resourcePath/" else "$resourcePath/?$query"
        return this.api.consume(url)
            .get()
            .response(ListSerializer(DMySparePart.serializer()))
    }

    /**
     * Obtiene el detalle de un repuesto personal.
     */
    override suspend fun getMySparePart(id: Long): DResponse<DMySparePart> {
        return this.api.consume("$resourcePath/$id/")
            .get()
            .response(DMySparePart.serializer())
    }

    /**
     * Crea un nuevo repuesto personal.
     */
    override suspend fun createMySparePart(part: DMySparePart): DResponse<DMySparePart> {
        return this.api.consume("$resourcePath/")
            .post(part, DMySparePart.serializer())
            .response(DMySparePart.serializer())
    }

    /**
     * Actualiza un repuesto personal existente (PUT).
     */
    override suspend fun updateMySparePart(id: Long, part: DMySparePart): DResponse<DMySparePart> {
        return this.api.consume("$resourcePath/$id/")
            .put(part, DMySparePart.serializer())
            .response(DMySparePart.serializer())
    }

    /**
     * Elimina (soft delete) un repuesto personal existente.
     */
    override suspend fun deleteMySparePart(id: Long): DResponse<Unit> {
        return this.api.consume(resourcePath)
            .delete(id.toInt())
            .response(Unit.serializer())
    }

    /**
     * Comparte un repuesto personal con otro usuario (crea una copia para él).
     *
     * A diferencia del resto de métodos, aquí se gestiona la respuesta a mano porque el 409
     * no es un error: debe devolverse como resultado tipado [DShareResult.AlreadyShared]
     * con el ID de la copia existente. El flujo normal (.response) convertiría el 409 en un
     * DResponse.Error y se perdería ese ID.
     */
    override suspend fun shareMySparePart(partId: Long, targetUserId: Int): DResponse<DShareResult> {
        val httpRequest = this.api.consume("$resourcePath/$partId/share/")
            .post(DShareSparePartRequest(targetUserId), DShareSparePartRequest.serializer())

        return try {
            val httpResponse = httpRequest.httpClient.request(httpRequest.request)
            when (httpResponse.status.value) {
                409 -> parseConflict(httpResponse.bodyAsText())
                in 200..299 -> parseShared(httpResponse.bodyAsText())
                else -> DResponse.Error(
                    extractBackendErrorMessage(httpResponse.bodyAsText(), httpResponse.status.value)
                )
            }
        } catch (e: ResponseException) {
            // Con expectSuccess=true el cliente lanza la excepción para 4xx y 5xx
            val body = try { e.response.bodyAsText() } catch (_: Throwable) { "" }
            if (e.response.status.value == 409) {
                parseConflict(body)
            } else {
                DResponse.Error(extractBackendErrorMessage(body, e.response.status.value), e)
            }
        } catch (e: ReceivePipelineException) {
            DResponse.Error("Error de red. No se puede conectar con el servidor.")
        } catch (e: CancellationException) {
            DResponse.Error(error = "Request cancelled", throwable = e)
        } catch (e: Throwable) {
            DResponse.Error(error = "Error inesperado: ${e.message ?: "Unknown"}", throwable = e)
        }
    }

    private fun buildQuery(
        vehicleVin: String?,
        brandId: Int?,
        modelId: Int?,
        search: String?
    ): String {
        val params = mutableListOf<String>()
        vehicleVin?.let { params.add("vehicle_vin=$it") }
        brandId?.let { params.add("brand_id=$it") }
        modelId?.let { params.add("model_id=$it") }
        search?.let { params.add("search=${it.encodeURLParameter(spaceToPlus = true)}") }
        return params.joinToString("&")
    }

    private fun parseShared(body: String): DResponse<DShareResult> {
        return try {
            val payload = api.json.decodeFromString(DShareSparePartResponse.serializer(), body)
            when (val sharedId = payload.sharedSparePartId) {
                null -> DResponse.Error(payload.error ?: payload.message ?: "Unexpected share response")
                else -> DResponse.Success(DShareResult.Shared(sharedId))
            }
        } catch (_: Throwable) {
            DResponse.Error("Error de serialización en respuesta de compartir")
        }
    }

    private fun parseConflict(body: String): DResponse<DShareResult> {
        return try {
            val payload = api.json.decodeFromString(DShareSparePartResponse.serializer(), body)
            when (val existingId = payload.sharedSparePartId) {
                null -> DResponse.Error(payload.error ?: "Unexpected conflict response")
                else -> DResponse.Success(DShareResult.AlreadyShared(existingId, payload.error))
            }
        } catch (_: Throwable) {
            DResponse.Error("Error de serialización en respuesta de conflicto")
        }
    }

    /**
     * Igual que en [link.erpango.quip.data.remote.ktor.HttpRequest]: nunca se devuelve el cuerpo
     * crudo, que para un 404 de nginx es una página HTML entera y acababa pintándose en la UI.
     */
    private fun extractBackendErrorMessage(responseBody: String, statusCode: Int): String {
        val fallback = "Error del servidor. Código: $statusCode"
        return try {
            val root = api.json.parseToJsonElement(responseBody).jsonObject
            root["error"]?.jsonPrimitive?.contentOrNull
                ?: root["message"]?.jsonPrimitive?.contentOrNull
                ?: root["detail"]?.jsonPrimitive?.contentOrNull // formato por defecto de DRF
                ?: fallback
        } catch (_: Throwable) {
            fallback
        }
    }
}
